import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  GaugeIcon,
  ChatCircleTextIcon,
  ListBulletsIcon,
  GearSixIcon,
  RobotIcon,
  PlugIcon,
  SignOutIcon,
  InstagramLogoIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const items = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, end: true, testid: "nav-dashboard" },
  { to: "/connections", label: "Connections", icon: PlugIcon, testid: "nav-connections" },
  { to: "/keywords", label: "Keywords", icon: ListBulletsIcon, testid: "nav-keywords" },
  { to: "/posts", label: "Posts", icon: InstagramLogoIcon, testid: "nav-posts" },
  { to: "/activity", label: "Activity", icon: ChatCircleTextIcon, testid: "nav-activity" },
  { to: "/settings", label: "Settings", icon: GearSixIcon, testid: "nav-settings" },
];

const SidebarInner = ({ onNav }) => {
  const { user, logout } = useAuth();
  return (
    <>
      <div className="mb-10 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-amber-500 text-zinc-950">
          <RobotIcon size={24} weight="fill" />
        </div>
        <div>
          <p className="font-display text-lg font-black uppercase tracking-tight text-white">
            Megatron
          </p>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            IG Automation SaaS
          </p>
        </div>
      </div>

      <nav className="flex flex-col gap-1">
        <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
          Workspace
        </p>
        {items.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNav}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                isActive
                  ? "bg-zinc-900 text-amber-500"
                  : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={18} weight={isActive ? "fill" : "duotone"} />
                <span>{label}</span>
                {isActive && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-amber-500" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto rounded-sm border border-zinc-800 bg-zinc-900 p-4" data-testid="sidebar-user">
        {user ? (
          <>
            <div className="flex items-center gap-3">
              {user.profile_pic ? (
                <img src={user.profile_pic} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-zinc-950">
                  {(user.name || "U").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-bold text-white">{user.name}</p>
                <p className="truncate text-[10px] text-zinc-500">{user.email || "Logged in"}</p>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-zinc-700 bg-transparent px-3 py-2 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:bg-zinc-800 hover:text-white"
            >
              <SignOutIcon size={14} weight="bold" /> Logout
            </button>
          </>
        ) : (
          <p className="text-xs text-zinc-500">Not signed in</p>
        )}
      </div>
    </>
  );
};

export const Sidebar = ({ mobileOpen, onClose }) => {
  const location = useLocation();

  // Close the drawer when route changes
  useEffect(() => {
    if (mobileOpen) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      {/* Desktop */}
      <aside
        className="fixed left-0 top-0 z-30 hidden h-full w-64 flex-col border-r border-zinc-800 bg-zinc-950 p-6 lg:flex"
        data-testid="sidebar"
      >
        <SidebarInner />
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-200 lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        data-testid="sidebar-backdrop"
      />
      <aside
        className={`fixed left-0 top-0 z-50 h-full w-72 max-w-[85vw] flex-col border-r border-zinc-800 bg-zinc-950 p-6 transition-transform duration-200 lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } flex`}
        data-testid="sidebar-mobile"
      >
        <button
          onClick={onClose}
          data-testid="sidebar-close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
          aria-label="Close menu"
        >
          <XIcon size={16} weight="bold" />
        </button>
        <SidebarInner onNav={onClose} />
      </aside>
    </>
  );
};
